import { useMemo } from "react";
import FirmFlowTable from "../../../../components/mytasks/FirmFlowTable"; // Adjust the import path as necessary
import { formatDate } from "../../../../helpers/formatDate";
import { getDueDateStatusFromDate } from "../../../../helpers/dueDateStatus";
import { sortDueDateRows } from "../../../../helpers/sortDueDateRows";
import {
    getNormalizedReportStatus,
    getReportStatusBadgeClass,
    getReportStatusLabel,
} from "../../../../helpers/reportStatusOptions";

const JobAccountabilityTable = ({
    onHandleClickNotes,
    data,
    canWriteSteps = false,
    canNavigateToService = true,
    canReadSteps = false,
    resetPageSignal,
}) => {
    const columns = useMemo(
        () => [
            {
                Header: "Department",
                accessor: "department.name",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Service",
                accessor: "clientServiceSubscriptionServiceName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Step",
                accessor: "name",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },

            {
                Header: "Assigned Date",
                accessor: "assignedDate",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {formatDate(props.value, {
                            dateStyle: "short",
                        })}
                    </a>
                ),
            },
            {
                Header: "Due Date",
                accessor: "dueDate",
                Cell: (props) => {
                    const { status, dueDateSubLabel, completionLabel } =
                        props.row.original;
                    const normalizedStatus = getNormalizedReportStatus(status);
                    const isComplete =
                        normalizedStatus === "complete" ||
                        normalizedStatus === "completed" ||
                        normalizedStatus === 2;
                    const isTodoOrInProgress =
                        normalizedStatus === "to do" ||
                        normalizedStatus === "in progress" ||
                        normalizedStatus === 0 ||
                        normalizedStatus === 1;
                    const badgeColorClass = isComplete
                        ? "bg-slate-500"
                        : getDueDateStatusFromDate(props.value)?.bgClass;

                    if (!props.value) {
                        return (
                            <span className="text-slate-500 whitespace-nowrap">
                                Not Assigned
                            </span>
                        );
                    }

                    return (
                        <a
                            rel="noreferrer"
                            href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                            className="inline-flex min-w-[110px] flex-col items-start whitespace-nowrap"
                        >
                            <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${
                                    badgeColorClass || "bg-slate-400"
                                }`}
                            >
                                {formatDate(props.value, {
                                    dateStyle: "short",
                                })}
                            </span>

                            {isTodoOrInProgress && dueDateSubLabel && (
                                <span className="mt-1 text-xs font-medium whitespace-nowrap text-slate-600">
                                    {dueDateSubLabel}
                                </span>
                            )}

                            {isComplete && completionLabel && (
                                <span className="mt-1 text-xs font-medium whitespace-nowrap text-slate-600">
                                    {completionLabel}
                                </span>
                            )}
                        </a>
                    );
                },
            },
            {
                Header: "Notes",
                accessor: "notes",
                Cell: (props) => (
                    <p
                        onClick={() => {
                            if (!canWriteSteps) return;
                            onHandleClickNotes({
                                jobId: props.row.original.id,
                                notes: props.value,
                            });
                        }}
                        className={`rounded-full text-gray ${
                            canWriteSteps
                                ? "cursor-pointer hover:text-blue-500"
                                : ""
                        }`}
                    >
                        {props.value ? props.value : "Click to add notes"}
                    </p>
                ),
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: (props) => {
                    const statusLabel = getReportStatusLabel(props.value);

                    return (
                        <a
                            rel="noreferrer"
                            href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                            className="inline-flex rounded-full whitespace-nowrap text-gray hover:text-blue-500"
                        >
                            <span
                                className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium text-white ${getReportStatusBadgeClass(
                                    props.value,
                                )}`}
                            >
                                {statusLabel}
                            </span>
                        </a>
                    );
                },
            },
        ],
        [onHandleClickNotes, canWriteSteps]
    );
    const sortedData = useMemo(
        () =>
            sortDueDateRows(data, {
                dueDateKeys: ["dueDate"],
                yearKeys: ["clientServiceSubscriptionYear", "serviceYear", "year"],
                serviceNameKeys: [
                    "clientServiceSubscriptionServiceName",
                    "serviceName",
                    "service",
                ],
            }),
        [data],
    );

    if (!canReadSteps) return null;

    return (
        <div
            onClickCapture={(event) => {
                if (
                    !canNavigateToService &&
                    event.target.closest("a")
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
            aria-disabled={!canNavigateToService}
        >
            {Array.isArray(data) && data.length > 0 ? (
                <FirmFlowTable
                    columns={columns}
                    data={sortedData}
                    resetPageSignal={resetPageSignal}
                />
            ) : (
                <p className="text-sm font-medium text-slate-500">
                    No steps found for the selected filters.
                </p>
            )}
        </div>
    );
};

export default JobAccountabilityTable;
